import { nextTick, onMounted, onBeforeUnmount, Ref, ref, computed, inject, watch } from "vue";
import lodashThrottle from "lodash-es/throttle";

import { ScreenSize } from "@contracts/screenSize";

export default function useScrollCurtains(
  targetContainer: Ref<HTMLElement>,
  visibilityThreshold = 10,
  screenSizes: Array<ScreenSize> = ["mobile", "tablet", "tablet-large", "laptop", "desktop", "desktop-wide"]
) {
  const isTopCurtainVisible = ref<boolean>(false);
  const isBottomCurtainVisible = ref<boolean>(false);

  const isLeftCurtainVisible = ref<boolean>(false);
  const isRightCurtainVisible = ref<boolean>(false);

  const screenSize = inject("screenSize") as Ref<ScreenSize>;

  function setCurtainsVisibility() {
    // horizontal
    if (!screenSizes.includes(screenSize.value) || !targetContainer?.value || targetContainer?.value.getBoundingClientRect().width === targetContainer?.value.scrollWidth) {
      isLeftCurtainVisible.value = false;
      isRightCurtainVisible.value = false;
    } else {
      isLeftCurtainVisible.value = targetContainer?.value?.scrollLeft >= visibilityThreshold;
      isRightCurtainVisible.value = targetContainer?.value?.scrollLeft <= targetContainer.value.scrollWidth - targetContainer.value.clientWidth - visibilityThreshold;
    }

    // vertical
    if (!screenSizes.includes(screenSize.value) || !targetContainer?.value || targetContainer?.value.getBoundingClientRect().height === targetContainer?.value.scrollHeight) {
      isTopCurtainVisible.value = false;
      isBottomCurtainVisible.value = false;
    } else {
      isTopCurtainVisible.value = targetContainer?.value?.scrollTop >= visibilityThreshold;
      isBottomCurtainVisible.value = targetContainer?.value?.scrollTop <= targetContainer.value.scrollHeight - targetContainer.value.clientHeight - visibilityThreshold;
    }
  }

  const setCurtainsVisibilityThrottled = lodashThrottle(setCurtainsVisibility, 100);

  async function setUp() {
    targetContainer.value?.addEventListener("scroll", setCurtainsVisibilityThrottled);
    window.addEventListener("resize", setCurtainsVisibilityThrottled);
    await nextTick();
    setTimeout(setCurtainsVisibility, 0);
  }

  async function kill() {
    targetContainer.value?.removeEventListener("scroll", setCurtainsVisibilityThrottled);
    window.removeEventListener("resize", setCurtainsVisibilityThrottled);
    await nextTick();
    setTimeout(setCurtainsVisibility, 0);
  }

  onMounted(async () => {
    if (targetContainer) {
      await setUp();
    }
  });

  watch(targetContainer, async () => {
    await kill();
    await setUp();
  });

  onBeforeUnmount(async () => {
    targetContainer.value?.removeEventListener("scroll", setCurtainsVisibilityThrottled);
    window.removeEventListener("resize", setCurtainsVisibilityThrottled);
    await nextTick();
    setTimeout(setCurtainsVisibility, 0);
  });

  watch(
    () => screenSize.value,
    async () => {
      await nextTick();
      setTimeout(setCurtainsVisibility, 0);
    }
  );

  return {
    isTop: isTopCurtainVisible,
    isBottom: isBottomCurtainVisible,
    isRight: isRightCurtainVisible,
    isLeft: isLeftCurtainVisible,
    setCurtainsVisibility,
  };
}
